/**
 * [T157] TxByCategoryBarSkin — `tx-by-category-bar` adapter.
 *
 * Resolver: categoryBreakdownFacet with groupBy=category.
 * @see https://recharts.org/en-US/api/BarChart
 * @see docs/design/T149-PR5-INSIGHTS-SKINS-SPEC.md §3.2
 */
import { BarChart2 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useCurrency } from '@/hooks/useCurrency';
import { WidgetFrame } from '../../WidgetFrame';
import type { SkinProps } from '../types';
import {
  skinTestId,
  SkinLoadingRows,
  SkinLoadingBars,
  SkinEmptyState,
  SkinErrorState,
  INSIGHTS_PALETTE,
  useSkinFrameProps,
} from './_shared';

const MIN_W = 3;
const MIN_H = 3;

export default function TxByCategoryBarSkin(props: SkinProps<'tx-by-category-bar'>) {
  const { widget, data, loading, error } = props;
  const { fmt } = useCurrency();
  const { width } = useWindowSize();
  const isMobile = (width ?? 1280) < 480;
  const isTooSmall = widget.w < MIN_W || widget.h < MIN_H;

  const slices = (data?.slices ?? [])
    .slice()
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 10);
  const isEmpty = !loading && !error && slices.length === 0;

  const config: ChartConfig = {
    amount: { label: 'Amount', color: 'hsl(var(--chart-2))' },
  };

  const chartData = slices.map((s) => ({
    name: s.emoji ? `${s.emoji} ${s.name}` : s.name,
    fullName: s.name,
    amount: s.amount,
    percentage: s.percentage,
  }));

  const frameProps = useSkinFrameProps(props);

  // Too-small fallback: top-3 categories ranked list
  if (isTooSmall) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Spending by Category'}
        icon={<BarChart2 className="h-4 w-4" />}
        {...frameProps}
        body={
          <div className="space-y-1 py-1">
            {loading ? (
              <SkinLoadingRows rows={3} />
            ) : slices.length === 0 ? (
              <p className="text-xs text-muted-foreground py-1 px-2">No data</p>
            ) : (
              slices.slice(0, 3).map((s, i) => (
                <div key={s.id} className="flex items-center gap-1 min-w-0 text-xs">
                  <span
                    className="shrink-0 w-2 h-2 rounded-full"
                    style={{ background: INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length] }}
                    aria-hidden="true"
                  />
                  <span className="truncate flex-1">{s.emoji ? `${s.emoji} ${s.name}` : s.name}</span>
                  <span className="shrink-0 font-medium">{fmt(s.amount)}</span>
                </div>
              ))
            )}
          </div>
        }
      />
    );
  }

  // Mobile fallback: top-5 list
  if (isMobile) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Spending by Category'}
        icon={<BarChart2 className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingRows rows={5} />
          ) : isEmpty ? (
            <div data-testid={skinTestId('tx-by-category-bar', widget.id, 'empty')}>
              <SkinEmptyState
                title="No spending data for this period"
                description="Add expense transactions to see category breakdown."
                cta={{ label: 'Add transaction', to: '/transactions' }}
              />
            </div>
          ) : (
            <ul className="space-y-1 w-full" role="list" aria-label="Spending by category">
              {slices.slice(0, 5).map((s, i) => (
                <li key={s.id} className="flex items-center gap-2 min-w-0 py-1.5 text-xs">
                  <span
                    className="shrink-0 w-2 h-2 rounded-full"
                    style={{ background: INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length] }}
                    aria-hidden="true"
                  />
                  <span className="break-words flex-1 min-w-0">
                    {s.emoji ? `${s.emoji} ${s.name}` : s.name}
                  </span>
                  <span className="shrink-0 font-medium">{fmt(s.amount)}</span>
                </li>
              ))}
            </ul>
          )
        }
      />
    );
  }

  // Main render
  return (
    <WidgetFrame
      id={widget.id}
      title={widget.title || 'Spending by Category'}
      icon={<BarChart2 className="h-4 w-4" />}
      {...frameProps}
      body={
        loading ? (
          <div data-testid={skinTestId('tx-by-category-bar', widget.id, 'loading')}>
            <SkinLoadingBars bars={5} />
          </div>
        ) : error ? (
          <div data-testid={skinTestId('tx-by-category-bar', widget.id, 'error')}>
            <SkinErrorState message={error.message} onRetry={props.onRetry} />
          </div>
        ) : isEmpty ? (
          <div data-testid={skinTestId('tx-by-category-bar', widget.id, 'empty')}>
            <SkinEmptyState
              title="No spending data for this period"
              description="Add expense transactions to see category breakdown."
              cta={{ label: 'Add transaction', to: '/transactions' }}
            />
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Spending by category: ${slices.length} categories`}
            className="w-full h-full min-w-0 min-h-0 overflow-hidden"
            data-testid={skinTestId('tx-by-category-bar', widget.id)}
          >
            <ChartContainer config={config} className="h-full w-full overflow-hidden">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 4, right: 40, top: 4, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tickFormatter={(v) =>
                    v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                  }
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  width={100}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: string) =>
                    v.length > 14 ? `${v.slice(0, 12)}\u2026` : v
                  }
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="amount" barSize={16} radius={[0, 4, 4, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
        )
      }
    />
  );
}
